import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import PayoutDetails from 'components/modules/accounting/payouts/payout-details';
import { Table, TableHeader, TableHead, TableRow, TableCell, TableBody, } from '@cargomatic/ui';
import Badge from 'components/v2/Badge';
import { humanize } from 'underscore.string';
import CargomaticModal from 'components/v2/modal';
import { getPayoutsForShipment } from 'utils/AccountingWebAPIUtils';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import SessionStore from 'stores/SessionStore';
import { canShowApproveAction, canShowDeleteAction } from 'helpers/accounting';
import { useTranslation } from 'react-i18next';
import formatMoney from 'utils/formatters/Money';
import ExternalLink from 'components/v2/ExternalLink';
import { sluggify } from 'utils/localization/utils';
import AdjustmentTitle from './AdjustmentTitle';
import AdjustmentAction from './AdjustmentAction';
import DateField from './DateField';
import AddBaseCostForm from './AddBaseCostForm';
import { fetchDocuments, handleDownloadRateCard } from 'utils/documentUtils';
import { showToast } from 'stores/ToastStore';
export default function CarrierCharges({ shipmentId, shipmentIsCurrent, charges, timezone, total, currency, isIntermodalShipmentType, onAddCustomAdjustment, onAddInternalAdjustment, onAddCustomerAdjustment, onAddBaseCost, onApproveBaseCost, onRemoveLineItem, carrierId, showDownloadRateCard, }) {
    var _a;
    const { t } = useTranslation();
    const [lineItems, setLineItems] = useState([]);
    const [selectedLineItem, setSelectedLineItem] = useState();
    const [selectedBaseCost, setSelectedBaseCost] = useState();
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showConfirmApprovalModal, setShowConfirmApprovalModal] = useState(false);
    const [showConfirmRemovalModal, setShowConfirmRemovalModal] = useState(false);
    const [recentRateCard, setRecentRateCard] = useState({});
    function handleHideDetailsModal() {
        setShowDetailsModal(false);
    }
    function handleApprove(lineItem) {
        return function () {
            setShowConfirmApprovalModal(true);
            setSelectedBaseCost(lineItem);
        };
    }
    function handleRemove(lineItem) {
        return function () {
            setShowConfirmRemovalModal(true);
            setSelectedLineItem(lineItem);
        };
    }
    function handleHideConfirmApprovalModal() {
        setShowConfirmApprovalModal(false);
        setSelectedLineItem(null);
        getPayouts();
    }
    function handleConfirmApproveBaseCost() {
        onApproveBaseCost(selectedBaseCost);
        handleHideConfirmApprovalModal();
    }
    async function handleConfirmLineItemRemoval() {
        await onRemoveLineItem(selectedLineItem, 'payables');
        handleHideConfirmRemovalModal();
    }
    function handleHideConfirmRemovalModal() {
        setShowConfirmRemovalModal(false);
        setSelectedLineItem(null);
    }
    async function getPayouts() {
        try {
            const { entity: { payouts }, } = await getPayoutsForShipment(shipmentId);
            const mappedLineItems = charges.map((lineItem) => {
                var _a;
                const isFirstFuelSurchargeItem = ((_a = charges.find((c) => c.charge_code === 'FSC')) === null || _a === void 0 ? void 0 : _a._id) === lineItem._id;
                const payout = payouts.find((p) => p._id === lineItem.payout_id);
                if (!payout) {
                    return Object.assign(Object.assign({}, lineItem), { payout: null, isFirstFuelSurchargeItem });
                }
                const { _id, number, timestamp, status, paid_date } = payout;
                return Object.assign(Object.assign({}, lineItem), { payout: {
                        _id,
                        number,
                        timestamp,
                        status,
                        paid_date,
                    }, isFirstFuelSurchargeItem });
            });
            /*
             * This section of code sorts the line item charges for easy
             * identification of quick pay line item, starting with the parent line
             * item, followed by relevant quick pay discounts, and so on.
             * This improves readabilty for the admin user
             */
            const parentCharges = mappedLineItems.filter((item) => !item.parent_adjustment_id);
            const childrenCharges = mappedLineItems.filter((item) => item.parent_adjustment_id);
            const childrenMap = childrenCharges.reduce((map, child) => {
                const parentId = child.parent_adjustment_id;
                if (!map[parentId]) {
                    map[parentId] = [];
                }
                map[parentId].push(child);
                return map;
            }, {});
            const arrangeLineItems = [];
            parentCharges.forEach((parent) => {
                arrangeLineItems.push(parent);
                // Check for children with `_id` as parent reference.
                if (childrenMap[parent._id]) {
                    arrangeLineItems.push(...childrenMap[parent._id]);
                }
                // Check for children with `accessorial_request_id` as parent reference.
                if (parent.accessorial_request_id &&
                    childrenMap[parent.accessorial_request_id]) {
                    arrangeLineItems.push(...childrenMap[parent.accessorial_request_id]);
                }
            });
            setLineItems(arrangeLineItems);
        }
        catch (e) {
            console.log('Error getting payouts', e);
        }
    }
    function renderPayoutLink(lineItem) {
        if (!lineItem.payout) {
            return null;
        }
        const { payout: { _id, number }, } = lineItem;
        return (React.createElement(ExternalLink, { dataTestId: "ExternalLink-8f6da654", href: `/payouts/${_id}`, title: number, showIcon: true }));
    }
    const isFinance = SessionStore.isFinance() || SessionStore.isFinanceAdmin();
    function showApproveButton(lineItem) {
        return isIntermodalShipmentType && canShowApproveAction(lineItem);
    }
    function showRemoveButton(lineItem) {
        return canShowDeleteAction(lineItem, isFinance, isIntermodalShipmentType);
    }
    useEffect(() => {
        getPayouts();
        if (carrierId)
            getRateCard();
    }, [charges]);
    const renderDownloadLink = () => {
        if (recentRateCard === null || recentRateCard === void 0 ? void 0 : recentRateCard.pdfUrl) {
            return (React.createElement("a", { onClick: () => handleDownloadRateCard(recentRateCard === null || recentRateCard === void 0 ? void 0 : recentRateCard.pdfUrl, true), className: "underline" }, t('LATEST_CARRIER_RATE_CARD')));
        }
        return React.createElement("p", { className: "text-red" }, t('NO_RATE_CARD_GENERATED'));
    };
    async function getRateCard() {
        try {
            const rateCardData = await fetchDocuments(carrierId, 'carrier_rate_card');
            setRecentRateCard(rateCardData);
        }
        catch (err) {
            console.error(err);
            showToast('', t('FAILED_TO_DOWNLOAD_RATE_CARD'), 'error');
        }
    }
    // Bens recommendation for accessorials beyond the 2 week period.
    // Some might be incorrectly marked but it was a trade off decision
    function getRateSource(lineItem) {
        if (!(lineItem === null || lineItem === void 0 ? void 0 : lineItem.rate_source)) {
            return (lineItem === null || lineItem === void 0 ? void 0 : lineItem.autorated) ? t('AUTOMATED') : t('MANUAL');
        }
        return t(sluggify(lineItem.rate_source));
    }
    function getColor(status) {
        const statusColorMap = {
            paid: 'green',
            approved_internally: 'blue',
            default: 'gray', // Default color
        };
        return statusColorMap[status] || statusColorMap.default;
    }
    function setChargeStatus(lineItem) {
        var _a, _b;
        if (lineItem === null || lineItem === void 0 ? void 0 : lineItem.payout) {
            return (React.createElement(Badge, { dataTestId: "Badge-df95852b", color: getColor((_a = lineItem.payout) === null || _a === void 0 ? void 0 : _a.status) }, humanize((_b = lineItem.payout) === null || _b === void 0 ? void 0 : _b.status)));
        }
        if (lineItem === null || lineItem === void 0 ? void 0 : lineItem.has_pending_state) {
            return (React.createElement(Badge, { dataTestId: "Badge-d114a320", color: "gray" }, humanize(t('PENDING'))));
        }
        return null;
    }
    return (React.createElement("div", { "data-testid": "div-d35e450b" },
        React.createElement("div", { className: "flex items-center gap-4 mb-4" },
            React.createElement("h4", { "data-testid": "h3-f3726b77", className: "text-base font-semibold" }, t('CARRIER_CHARGES')),
            showDownloadRateCard && renderDownloadLink()),
        React.createElement("div", { "data-testid": "div-763fa60e", className: "overflow-x-auto" },
            React.createElement(Table, { "data-testid": "table-3bab90ce", className: "" },
                React.createElement(TableHeader, { "data-testid": "thead-24d618d2" },
                    React.createElement(TableRow, { "data-testid": "tr-d37e1215" },
                        React.createElement(TableHead, { "data-testid": "th-b8048929", className: "w-48" }, t('CHARGE')),
                        React.createElement(TableHead, { "data-testid": "th-f0ade268", className: "w-20" }, t('COST')),
                        React.createElement(TableHead, { "data-testid": "th-314678fe", className: "w-40" }, t('ENTERED_DATE')),
                        React.createElement(TableHead, { "data-testid": "th-21a3223c", className: "w-40" }, t('RATE_SOURCE')),
                        React.createElement(TableHead, { "data-testid": "th-b02300c0", className: "w-52" }, t('APPROVED_PAYOUT')),
                        React.createElement(TableHead, { "data-testid": "th-aca19183", className: "w-40" }, t('APPROVED_PAYOUT_DATE')),
                        React.createElement(TableHead, { "data-testid": "th-2f1e7db9", className: "w-24" }, t('PAYOUT_STATUS')),
                        React.createElement(TableHead, { "data-testid": "th-b73bb107", className: "w-32" }, t('PAID_DATE')),
                        React.createElement(TableHead, { "data-testid": "th-b2590f55" }, "\u00A0"))),
                React.createElement(TableBody, { "data-testid": "tbody-c5ceb454" },
                    lineItems.map((lineItem, index) => {
                        var _a, _b, _c;
                        return (React.createElement(TableRow, { "data-testid": `tr-75a4ad3d-${index}`, key: lineItem._id },
                            React.createElement(TableCell, { "data-testid": `td-fcb8f8ec-${index}`, className: "break-words" },
                                React.createElement(AdjustmentTitle, { lineItem: lineItem, unapplied: isIntermodalShipmentType && !lineItem.equipment })),
                            React.createElement(TableCell, { "data-testid": `td-042de8b8-${index}` }, formatMoney((_a = lineItem.value) === null || _a === void 0 ? void 0 : _a.toFixed(2), lineItem === null || lineItem === void 0 ? void 0 : lineItem.currency)),
                            React.createElement(TableCell, { "data-testid": `td-2b5a7029-${index}` },
                                React.createElement(DateField, { date: lineItem.timestamp, timezone: timezone })),
                            React.createElement(TableCell, { "data-testid": `td-d4fdca1f-${index}`, className: "text-capitalize" }, getRateSource(lineItem)),
                            React.createElement(TableCell, { "data-testid": `td-50403cb8-${index}` }, renderPayoutLink(lineItem)),
                            React.createElement(TableCell, { "data-testid": `td-ed299596-${index}` },
                                React.createElement(DateField, { date: (_b = lineItem.payout) === null || _b === void 0 ? void 0 : _b.timestamp, timezone: timezone })),
                            React.createElement(TableCell, { "data-testid": `td-da5052a8-${index}` }, setChargeStatus(lineItem)),
                            React.createElement(TableCell, { "data-testid": `td-89c7346e-${index}` },
                                React.createElement(DateField, { date: (_c = lineItem.payout) === null || _c === void 0 ? void 0 : _c.paid_date, timezone: timezone, showTime: false })),
                            React.createElement(TableCell, { "data-testid": `td-4a199dc5-${index}` },
                                React.createElement(AdjustmentAction, { lineItem: lineItem, shipmentIsCurrent: shipmentIsCurrent, isIntermodalShipmentType: isIntermodalShipmentType, onAddCustomAdjustment: onAddCustomAdjustment, onAddCustomerAdjustment: onAddCustomerAdjustment, onAddInternalAdjustment: onAddInternalAdjustment, showApprove: showApproveButton(lineItem), onApprove: handleApprove(lineItem), showRemove: showRemoveButton(lineItem), onRemove: handleRemove(lineItem) }))));
                    }),
                    React.createElement(TableRow, { "data-testid": "tr-2145a9b9" },
                        React.createElement(TableCell, { "data-testid": "td-dec834fa" },
                            React.createElement("div", { "data-testid": "div-0cffa7dc", className: "w-48" }, t('TOTAL'))),
                        React.createElement(TableCell, { "data-testid": "td-72d7df69", className: "font-bold" }, formatMoney(total, (_a = lineItems[0]) === null || _a === void 0 ? void 0 : _a.currency)),
                        React.createElement(TableCell, { "data-testid": "td-07c4d5f1", colSpan: 7 }, "\u00A0")))),
            React.createElement(AddBaseCostForm, { ledgerType: "payables", ledgers: charges, onAddBaseCost: onAddBaseCost, currency: currency })),
        showDetailsModal && (React.createElement(CargomaticModal, { dataTestId: "CargomaticModal-8ae21013", title: "", show: showDetailsModal, footer: null, body: React.createElement(PayoutDetails, { payoutId: selectedLineItem }), onHide: handleHideDetailsModal, dialogClassName: "modal-override", location: "top" })),
        showConfirmApprovalModal && (React.createElement(ConfirmModal, { dataTestId: "ConfirmModal-1e376964", show: showConfirmApprovalModal, onConfirm: handleConfirmApproveBaseCost, onHide: handleHideConfirmApprovalModal, title: t('APPROVE_CARRIER_COST_TITLE'), message: t('APPROVE_CARRIER_COST_MESSAGE'), affirmative: t('APPROVE'), hideButtonText: t('NO') })),
        showConfirmRemovalModal && (React.createElement(ConfirmModal, { dataTestId: "ConfirmModal-2fbe9ab9", show: showConfirmRemovalModal, onConfirm: handleConfirmLineItemRemoval, onHide: handleHideConfirmRemovalModal, title: t('APPROVE_ITEM_REMOVAL_TITLE'), message: t('APPROVE_ITEM_REMOVAL_MESSAGE'), affirmative: t('APPROVE'), hideButtonText: t('NO') }))));
}
CarrierCharges.propTypes = {
    timezone: PropTypes.string,
};
CarrierCharges.defaultProps = {
    timezone: 'America/Los_Angeles',
};
